<template>
  <form @submit.prevent="handleSaveAccountNameButtonClick">
    <h2 class="setting-title">
      {{ $t('corpSettingApi.user') }}
    </h2>
    <BListItem class="mb-300">
      <template #header>
        <label for="client-slug">{{ $t('client.slug') }}</label>
      </template>
      <BInput
        :model-value="client?.slug"
        input-id="client-slug"
        class="w-input"
        disabled
      />
    </BListItem>
    <BListItem class="mb-300">
      <template #header>
        <label for="client-name">{{ $t('client.name') }}</label>
      </template>
      <BInput
        v-model="internalClientName"
        v-model:valid="clientNameValid"
        input-id="client-name"
        class="w-input"
        :disabled="!authorize(['owner'])"
        :rules="[requiredRule]"
      />
    </BListItem>
    <BListItem class="mt-400">
      <BBtn
        :disabled="!clientNameValid || !authorize(['owner'])"
        type="primary"
        btn-type="submit"
        :loading="$wait.is('updateClientWait')"
      >
        {{ $t('general.save.text') }}
      </BBtn>
    </BListItem>
  </form>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useErrorHandler } from '@/composable/error-handler';
import { useInputValidations } from '@/composable/input-validations';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store';

const i18n = useI18n();
const { doActionWithWait } = useWait();
const toast = inject(KEY_OF_TOAST);

const { defaultHandler } = useErrorHandler();
const { requiredRule } = useInputValidations();
const { inject: injectCurrentUser } = useCurrentUserProvider();
const { authorize, currentUser } = injectCurrentUser();

const store = useStore(key);
const client = computed(() => store.getters['user/client']);
const internalClientName = ref<string>('');
const clientNameValid = ref<boolean>(false);

watch(client, (newVal) => {
  internalClientName.value = newVal?.name ?? '';
}, { immediate: true });

const fetchClient = async () => {
  await doActionWithWait('fetchClientWait', async () => {
    await store.dispatch('user/getClientAction', { id: currentUser.value?.clientId });
  });
};
onBeforeMount(async () => {
  await fetchClient();
});

const handleSaveAccountNameButtonClick = async () => {
  const clientId = client.value?.id;
  if (!clientId) return;
  if (!clientNameValid.value) return;

  await doActionWithWait('updateClientWait', async () => {
    await store.dispatch('user/updateClientAction', {
      id: clientId,
      body: {
        client: {
          name: internalClientName.value,
        },
      },
      errorHandlers: {
        422: defaultHandler,
      },
    });
  });
  toast.show(
    i18n.t('general.done.withTarget', {
      action: i18n.t('general.update.text'),
      target: i18n.t('client.name'),
    }),
  );
};
</script>

<style lang="scss" scoped>
.setting-title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;

  &:not(:first-child) {
    margin-top: 80px;
  }
}
.w-input {
  max-width: 540px;
}
</style>
